import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  Calculator, Coffee, LayoutGrid, GraduationCap, TrendingUp, Target, BarChart3,
  ClipboardList, Wrench, MapPin, LineChart, Store, MessageCircle, Video, Users,
  CheckCircle,
} from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import heroImg from "@/assets/apertura-hero.jpg";
import numerosImg from "@/assets/apertura-numeros.jpg";
import barraImg from "@/assets/consultoria-barra.jpg.asset.json";
import formacionImg from "@/assets/consultoria-formacion.png.asset.json";
import rentabilidadImg from "@/assets/apertura-rentabilidad.jpg";

const stats = [
  { value: "360º", label: "Técnica, operativa y números" },
  { value: "1 día", label: "Formación presencial" },
  { value: "España", label: "Base en Málaga" },
];


const pillars: { img: string; title: string; desc: string; points: string[] }[] = [
  {
    img: rentabilidadImg,
    title: "Rentabilidad y números reales",
    desc: "Escandallos, precios de venta, márgenes y punto de equilibrio. Sabrás desde el principio si tu cafetería es rentable.",
    points: ["Coste real de cada producto", "Break-even y facturación mínima", "KPI para controlar gastos y mermas"],
  },
  {
    img: barraImg.url,
    title: "Maquinaria y diseño de barra",
    desc: "Invertir solo en lo que necesitas y organizar la barra para despachar rápido, con menos esfuerzo y más ventas.",
    points: ["Maquinaria adaptada a tu volumen", "Workflow ergonómico en horas punta", "Distribución pensada para el equipo"],
  },
  {
    img: formacionImg.url,
    title: "Formación y estandarización",
    desc: "Recetas y procedimientos iguales para todos: la misma calidad en cada taza, la prepare quien la prepare.",
    points: ["Formación barista, espresso y latte art", "Fichas de recetas y procesos", "Seguimiento y mejora continua"],
  },
];

const URL = "https://federicoduronio.com/consultoria-apertura-cafeterias";
const TITLE = "Consultoría para abrir cafeterías en España | Federico Duronio";
const DESC =
  "Consultor de cafeterías en Málaga y toda España: apertura, costes, precios, rentabilidad, maquinaria, diseño de barra y formación barista.";
const CITIES_MALAGA = ["Málaga", "Marbella", "Fuengirola", "Torremolinos", "Benalmádena", "Mijas", "Estepona", "Nerja", "Vélez-Málaga", "Rincón de la Victoria", "Antequera", "Ronda"];
const CITIES_SPAIN = ["Madrid", "Barcelona", "Valencia", "Sevilla", "Granada", "Córdoba", "Cádiz", "Almería", "Jaén", "Huelva", "Alicante", "Murcia", "Zaragoza", "Bilbao", "Palma de Mallorca", "Las Palmas"];
const WHATSAPP =
  "https://wa.me/34606649183?text=" +
  encodeURIComponent("Hola Federico, quiero información sobre la consultoría para abrir mi cafetería.");

const problems = [
  "¿Cuánto tienes que facturar al mes para no perder dinero?",
  "¿Cuánto necesitas vender cada día y con qué ticket medio?",
  "¿Tus precios de venta cubren de verdad el coste de cada producto?",
  "¿Estás comprando maquinaria que no necesitas o que se queda corta?",
  "¿Tu barra te hace perder tiempo y ventas en las horas punta?",
  "¿Cada persona del equipo prepara el café de una forma distinta?",
];

const services = [
  { icon: Store, title: "Asesoría para abrir una cafetería", desc: "Acompañamiento completo, de la idea a la apertura, para abrir una cafetería rentable con criterio." },
  { icon: MapPin, title: "Análisis preliminar del proyecto y ubicación", desc: "Estudio del concepto, el local, la zona y el tipo de cliente antes de invertir." },
  { icon: Wrench, title: "Inversión inteligente y selección de maquinaria", desc: "Maquinaria para cafeterías y herramientas adecuadas a tu volumen real, sin sobrecostes." },
  { icon: LayoutGrid, title: "Diseño y distribución de barra", desc: "Diseño y organización de barra, workflow y productividad: trabajar más rápido para vender más." },
  { icon: Calculator, title: "Costes, escandallos y precios de venta", desc: "Te enseño a calcular los precios de tu cafetería a partir del coste real de cada producto." },
  { icon: TrendingUp, title: "Rentabilidad, márgenes y break-even", desc: "Costes y márgenes de la cafetería y punto de equilibrio: cuánto necesitas para empezar a ganar." },
  { icon: Target, title: "Ticket medio y objetivos de venta", desc: "Facturación mínima mensual y diaria, ticket medio necesario y estrategias para subirlo." },
  { icon: BarChart3, title: "Control de gastos, mermas y KPI", desc: "Indicadores claros para saber dónde está fallando el negocio y dónde se puede mejorar." },
  { icon: GraduationCap, title: "Formación barista, espresso y latte art", desc: "Formación barista para empresas: una base estándar para que todo el equipo trabaje bien." },
  { icon: ClipboardList, title: "Estandarización de recetas y procedimientos", desc: "Recetas, fichas y procesos iguales para todos, con calidad constante en cada taza." },
  { icon: Coffee, title: "Optimización de cafeterías existentes", desc: "Si ya estás abierto, analizamos tus números y tu barra para mejorar resultados." },
];

const audiences = [
  "Nuevas aperturas", "Cafeterías de especialidad", "Coffee shops", "Brunch cafés",
  "Negocios takeaway", "Cafeterías que quieren mejorar su funcionamiento",
];

const faqs = [
  { q: "¿Cuánto cuesta abrir una cafetería en España?", a: "Depende del local, la obra y la maquinaria. Por eso empezamos por un análisis preliminar del proyecto: calculamos la inversión necesaria y te ayudamos a no gastar en maquinaria o mobiliario que no necesitas." },
  { q: "¿Cómo saber si mi cafetería será rentable?", a: "Calculamos tus gastos fijos, el punto de equilibrio (break-even), la facturación mínima mensual y diaria y el ticket medio necesario. Con mi hoja de cálculo propia ves con tus números reales si el negocio funciona antes de abrir." },
  { q: "¿Cómo se calculan los precios de una cafetería?", a: "Con escandallos: el coste real de cada producto (café, leche, vaso, mermas) y el margen que necesitas. Así cada precio de la carta cubre sus costes y aporta beneficio." },
  { q: "¿Qué maquinaria necesita una cafetería?", a: "La que corresponde a tu volumen real de ventas: máquina de espresso, molinos, agua y equipamiento de barra. Te asesoro para elegir maquinaria para cafeterías adecuada, sin quedarte corto ni pagar de más." },
  { q: "¿Cómo debe ser el diseño de la barra de una cafetería?", a: "Ergonómico y pensado para el flujo de trabajo: cada elemento en su sitio para despachar rápido en horas punta, con menos esfuerzo y menos errores." },
  { q: "¿Ofreces formación barista para empresas y equipos?", a: "Sí. Una jornada presencial de formación barista, espresso y latte art para todo el equipo, con recetas y procedimientos estandarizados para que cada taza salga igual." },
  { q: "¿Trabajas solo en Málaga?", a: "Mi base está en Málaga, pero ofrezco asesoría de cafeterías en toda España: Madrid, Barcelona, Valencia, Sevilla, Marbella y cualquier ciudad, combinando videollamada, WhatsApp y una jornada presencial." },
  { q: "¿Puedes ayudarme si mi cafetería ya está abierta?", a: "Sí. En la optimización de cafeterías existentes analizamos tus costes y márgenes, mermas, KPI y organización de barra para mejorar los resultados del negocio." },
];

const schema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "Consultoría para abrir cafeterías — Federico Duronio",
  url: URL,
  description: DESC,
  telephone: "+34606649183",
  address: { "@type": "PostalAddress", addressLocality: "Málaga", addressRegion: "Andalucía", addressCountry: "ES" },
  areaServed: [
    { "@type": "Country", name: "España" },
    { "@type": "AdministrativeArea", name: "Andalucía" },
    { "@type": "AdministrativeArea", name: "Costa del Sol" },
    ...[...CITIES_MALAGA, ...CITIES_SPAIN].map((name) => ({ "@type": "City", name })),
  ],
  provider: { "@type": "Person", name: "Federico Duronio", url: "https://federicoduronio.com/" },
  serviceType: "Consultoría y asesoría de apertura de cafeterías",
  knowsAbout: [
    "Consultoría para abrir cafetería", "Rentabilidad de una cafetería", "Escandallos y precios de venta",
    "Punto de equilibrio", "Maquinaria para cafeterías", "Diseño de barra", "Formación barista para empresas",
    "Optimización de cafeterías", "Café de especialidad", "Latte art",
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Servicios de consultoría para cafeterías",
    itemListElement: services.map((s) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: s.title, description: s.desc } })),
  },
};

const breadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Inicio", item: "https://federicoduronio.com/home" },
    { "@type": "ListItem", position: 2, name: "Consultoría apertura cafeterías", item: URL },
  ],
};

const ConsultoriaApertura = () => (
  <div className="min-h-screen bg-background">
    <Helmet>
      <title>{TITLE}</title>
      <meta name="description" content={DESC} />
      <link rel="canonical" href={URL} />
      <link rel="alternate" hrefLang="es-ES" href={URL} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:site_name" content="Federico Duronio" />
      <meta property="og:title" content={TITLE} />
      <meta property="og:description" content={DESC} />
      <meta property="og:url" content={URL} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={TITLE} />
      <meta name="twitter:description" content={DESC} />
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
    </Helmet>
    <Navigation />

    <main>
      {/* Presentación */}
      <section className="relative pt-36 pb-24 text-cream overflow-hidden">
        <img src={heroImg} alt="Barra de una cafetería de especialidad" width={1600} height={912} className="absolute inset-0 w-full h-full object-cover scale-105 animate-[pulse_12s_ease-in-out_infinite]" />
        <div className="absolute inset-0 bg-gradient-to-br from-espresso/90 via-espresso/75 to-warmBrown/70" />
        <div className="relative container mx-auto px-4 max-w-4xl text-center animate-fade-in">
          <p className="text-gold uppercase tracking-widest text-sm font-semibold mb-4">
            Consultor de cafeterías · Málaga y toda España
          </p>
          <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
            Consultoría para abrir una cafetería rentable y bien organizada
          </h1>
          <p className="text-lg md:text-xl text-cream/85 leading-relaxed mb-10">
            Te ayudo a planificar y optimizar la parte técnica, operativa y económica de tu cafetería. Desde la
            selección de maquinaria y el diseño de barra hasta el cálculo de costes, precios, márgenes, formación del
            equipo y seguimiento de resultados. Mi objetivo es ayudarte a invertir con criterio y construir un negocio
            eficiente, organizado y económicamente sostenible.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="premium" size="lg" asChild>
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 h-5 w-5" /> Hablar por WhatsApp
              </a>
            </Button>
            <Button variant="outline" size="lg" asChild className="bg-transparent border-cream/40 text-cream hover:bg-cream/10 hover:text-cream">
              <Link to="/home#contacto">Solicitar información</Link>
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-14 max-w-2xl mx-auto">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl border border-gold/30 bg-espresso/40 backdrop-blur p-4">
                <p className="text-2xl md:text-3xl font-bold text-gold">{s.value}</p>
                <p className="text-xs md:text-sm text-cream/80">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problemas */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl md:text-4xl font-bold text-foreground text-center mb-4">
            Cómo abrir una cafetería rentable: empieza por los números
          </h2>
          <p className="text-center text-muted-foreground max-w-3xl mx-auto mb-12 text-lg">
            Muchas cafeterías abren sin saber cuánto tienen que vender para ser rentables. Antes de invertir, estas son
            las preguntas que respondemos juntos:
          </p>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="grid gap-3">
              {problems.map((p, i) => (
                <Card key={p} className="p-4 flex gap-4 items-center hover:translate-x-2 hover:border-gold/60 transition-all duration-300">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold font-bold">{i + 1}</span>
                  <p className="text-foreground">{p}</p>
                </Card>
              ))}
            </div>
            <div className="relative group rounded-2xl overflow-hidden shadow-2xl">
              <img src={numerosImg} alt="Cálculo de rentabilidad de una cafetería" loading="lazy" width={1024} height={1024} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-espresso/95 to-transparent p-6 text-cream">
                <h3 className="text-xl font-bold mb-2">La rentabilidad, con datos reales</h3>
                <p className="text-cream/85 text-sm leading-relaxed">
                  Con mi herramienta de cálculo propia introduces tus números y ves si tu cafetería funciona:
                  facturación mínima, ticket medio necesario y dónde se pierde dinero.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pilares con imagen */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-6xl space-y-20">
          {pillars.map((p, i) => (
            <div key={p.title} className={`grid lg:grid-cols-2 gap-10 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <div className="group rounded-2xl overflow-hidden shadow-2xl aspect-[4/3]">
                <img src={p.img} alt={p.title} loading="lazy" width={1024} height={1024} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              </div>
              <div>
                <span className="text-gold font-semibold uppercase tracking-widest text-sm">Pilar {i + 1}</span>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mt-2 mb-4">{p.title}</h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">{p.desc}</p>
                <ul className="space-y-2">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex gap-3 items-start text-foreground">
                      <CheckCircle className="h-5 w-5 text-gold shrink-0 mt-0.5" /> {pt}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Servicios */}
      <section className="py-20 bg-muted/40">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-4xl font-bold text-foreground text-center mb-4">
            Asesoría de apertura de cafeterías en España: qué incluye
          </h2>
          <div className="w-24 h-1 bg-gold mx-auto mb-12" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map(({ icon: Icon, title, desc }) => (
              <Card key={title} className="p-6 h-full hover:shadow-xl hover:-translate-y-1 hover:border-gold/60 transition-all duration-300 group">
                <div className="h-14 w-14 rounded-xl bg-gold/15 flex items-center justify-center mb-4 group-hover:bg-gold/25 transition-colors"><Icon className="h-7 w-7 text-gold" /></div>
                <h3 className="text-lg font-bold text-foreground mb-2">{title}</h3>
                <p className="text-muted-foreground leading-relaxed">{desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Formato */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl md:text-4xl font-bold text-foreground text-center mb-12">
            Cómo trabajamos: un servicio 360º
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6">
              <Users className="h-8 w-8 text-gold mb-4" />
              <h3 className="text-lg font-bold text-foreground mb-2">Un día presencial</h3>
              <p className="text-muted-foreground">Jornada de formación del equipo en barra, con un estándar común de trabajo.</p>
            </Card>
            <Card className="p-6">
              <Video className="h-8 w-8 text-gold mb-4" />
              <h3 className="text-lg font-bold text-foreground mb-2">Videollamada y WhatsApp</h3>
              <p className="text-muted-foreground">Análisis, números, maquinaria y barra se trabajan a distancia, estés donde estés.</p>
            </Card>
            <Card className="p-6">
              <LineChart className="h-8 w-8 text-gold mb-4" />
              <h3 className="text-lg font-bold text-foreground mb-2">Herramienta para ver dónde va tu negocio</h3>
              <p className="text-muted-foreground">Te dejo herramienta para ver dónde va tu negocio: tus números, claros y bajo control.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Zona y clientes */}
      <section className="py-20 bg-muted/40">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">
            Asesoría de cafeterías en Málaga y en toda España
          </h2>
          <p className="text-lg text-muted-foreground mb-10">
            Estoy de base en Málaga, pero trabajo en toda España: combinamos videollamada, WhatsApp y una jornada
            presencial de formación, estés donde estés.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {audiences.map((a) => (
              <span key={a} className="px-4 py-2 rounded-full border border-gold/50 text-foreground bg-background">
                {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Preguntas frecuentes */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-bold text-foreground text-center mb-4">
            Preguntas frecuentes sobre abrir una cafetería
          </h2>
          <div className="w-24 h-1 bg-gold mx-auto mb-12" />
          <div className="space-y-4">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl border border-border bg-card p-5 open:border-gold/60 transition-colors">
                <summary className="cursor-pointer list-none flex justify-between items-center gap-4 font-semibold text-foreground">
                  <h3 className="text-base md:text-lg">{f.q}</h3>
                  <span className="text-gold text-2xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-espresso to-warmBrown text-cream">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-2xl md:text-4xl font-bold mb-4">¿Vas a abrir una cafetería o quieres mejorar la tuya?</h2>
          <p className="text-cream/85 text-lg mb-8">
            Cuéntame tu proyecto y vemos juntos qué necesitas para que sea rentable desde el primer día.
          </p>
          <Button variant="premium" size="lg" asChild>
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="mr-2 h-5 w-5" /> Escríbeme por WhatsApp
            </a>
          </Button>
        </div>
      </section>
    </main>

    <Footer />
    <WhatsAppButton />
  </div>
);

export default ConsultoriaApertura;
